import { useState } from "react";

interface FaqItem {
  q: string;
  a: string | string[];
}

interface FaqSection {
  label: string;
  items: FaqItem[];
}

const FAQ: FaqSection[] = [
  {
    label: "Platform & Data",
    items: [
      {
        q: "What data feeds the dashboard?",
        a: "The platform reads incrementally from D365 F&O via Synapse Link into Azure Data Lake Gen2 and serves analytics-ready tables to the dashboard. The internal source-of-record covers projects, billing, AR, change orders, GL, and customer master. External signals (BLS PPI, FRED, Dodge, GDELT, D&B, PACER, NOAA, and 100+ more) are layered as covariates and distress flags.",
      },
      {
        q: "How fresh is the data?",
        a: "Cadence varies by surface. Executive views refresh every 5–15 minutes; AR aging refreshes in near-real time on settled events; macro covariates refresh on the underlying source's natural cadence (daily for FRED, weekly for SLOOS, monthly for AIA ABI). The Live indicator in the topbar shows the last successful re-derive timestamp.",
      },
      {
        q: "What's the system of record?",
        a: "Dynamics 365 F&O. The platform never rewrites D365 — it reads the canonical entities (ProjTable, ProjJournalTrans, ProjInvoiceJour, CustTable, GeneralJournalAccountEntry, etc.) and produces an analytics layer. Every surfaced number ladders back to a named D365 column via the canonical data model.",
      },
      {
        q: "How are forecasts validated?",
        a: "Walk-forward, group-aware (project-grouped) backtests on closed periods. Coverage of 50/80/95 conformal prediction intervals is tracked monthly. Out-of-bounds coverage triggers a conformal recalibration. Forecast accuracy targets: ±5% annual net income, < 10% quarterly miss, 80% PI coverage in [75%, 85%].",
      },
    ],
  },
  {
    label: "Indicators & Alerts",
    items: [
      {
        q: "What's the difference between a KPI and a KRI?",
        a: "A KPI (Key Performance Indicator) measures performance — usually directional (higher / lower is better) and tied to a target. A KRI (Key Risk Indicator) measures risk exposure — typically a composite or threshold-triggered signal where a breach demands action. K-001 (over/underbilling) is a KPI; K-028 (Customer Risk Tier) is a KRI.",
      },
      {
        q: "How are Critical / High / Medium / Low impact tiers assigned?",
        a: [
          "Critical — directly determines a POC success measure, board-level decision, or material cash event (>$10M scale). Hero tile, real-time alert, daily refresh.",
          "High — drives operational decisions for finance leadership / branch leads. Operational dashboard, near-real-time refresh.",
          "Medium — informs monthly / quarterly review, segment color, secondary signal. Monthly review dashboard.",
          "Low — contextual / external / process compliance. Quarterly review only.",
        ],
      },
      {
        q: "How are alert thresholds set?",
        a: "Thresholds are derived from each metric's historical distribution and tier-specific peer baselines, not from absolute cutoffs. A 2σ breach on the trailing-24-month baseline is the typical trigger; thresholds are tunable per Client appetite.",
      },
      {
        q: "What does the σ ring on the CPI / SPI scatter mean?",
        a: "The inner ring at 1.0σ marks the in-control band; the outer ring at 2.0σ marks the watchlist boundary. Projects beyond 2.0σ from (1, 1) are escalation candidates. σ defaults to 0.10 — a typical construction earned-value variance — and is calibratable per archetype.",
      },
    ],
  },
  {
    label: "Forecasting & Models",
    items: [
      {
        q: "What forecasting methodology powers the Net Income Forecast?",
        a: "A hierarchical reconciliation: per-project margin-fade forecasts (cost-to-cost % complete + EAC drift trajectory) roll up to the enterprise. Macro overlays (ABI, PPI, FRED rates, SLOOS) enter as covariates with documented lag structure. Conformal prediction intervals are calibrated on out-of-sample residuals — not Gaussian assumptions.",
      },
      {
        q: "How does the customer risk score (K-028) work?",
        a: "K-028 is a deterministic additive model over 11 features: Paydex level + drift, lien count, lawsuit / bankruptcy flag, AR concentration, pay-cycle drift, dispute rate, write-off history, days-past-due median, contract-type risk, branch risk, and tier concentration. SHAP-style feature contribution is direct (linear additive). Tiers: Critical (≥75), High (50–74), Medium (25–49), Low (<25).",
      },
      {
        q: "Are these models black-box?",
        a: "No. Every forecast surfaces its top-N driver decomposition, and every score surfaces its per-feature contribution. The brief's explainability mandate is built into every model: no aggregate without a drill-through to its drivers in ≤ 2 clicks.",
      },
      {
        q: "How are models retrained?",
        a: "Drift-triggered automated retraining. Population Stability Index (PSI) on top-k features is monitored weekly; PSI > 0.25 triggers a retrain evaluation. Champion / challenger comparison runs on a 90-day rolling holdout; promotion is gated on accuracy and coverage maintenance.",
      },
    ],
  },
  {
    label: "Security & Compliance",
    items: [
      {
        q: "How is data secured?",
        a: "AES-256 at rest with customer-managed keys via Azure Key Vault; TLS 1.2+ in transit. Per-Client tenant isolation at the ADLS folder level. Identity is Entra ID with MSAL.js PKCE for the SPA and OBO for backend service calls. Microsoft Purview classifies data; Microsoft Sentinel handles SIEM and audit logging.",
      },
      {
        q: "Is there a SOC 2 attestation?",
        a: "SOC 2 Type II controls are designed and mapped against the Client's compliance frame. External attestation is scoped post-launch. The control register is reviewed quarterly.",
      },
      {
        q: "Where does my data live?",
        a: "All Client data resides in the Client's Azure tenant — there is no shared-services data plane between Clients. The platform is deployed per-tenant with Private Endpoints, VNet integration, NSGs, and an Application Gateway WAF.",
      },
      {
        q: "Who owns the IP?",
        a: "Capabilities developed for the Client are Client-owned at delivery, per the engagement agreement. The platform's data-source catalog and the underlying methodology stay with the platform vendor; the configurations, calibrations, and Client-specific extensions transfer.",
      },
    ],
  },
  {
    label: "Using the Dashboard",
    items: [
      {
        q: "What's the recommended demo order?",
        a: [
          "1. Overview — board-level cash and risk position",
          "2. Portfolio Billing — over/underbilling forecast + CPI/SPI earned-value scatter",
          "3. AR & Customer Risk — DSO drift + watchlist + distress timeline",
          "4. Customer 360 — drill into a flagged customer",
          "5. Net Income Forecast — 24-month rolling with conformal PI",
          "6. Decision Pack — banking-grade covenant package",
          "7. Industry — sectoral signal context",
          "8. Zen Insights — auto-synthesised executive brief",
        ],
      },
      {
        q: "How do I drill from a number to its drivers?",
        a: "Every primary metric tile has an info icon (i) for definition + source, and a click target on the value itself for drill-through. From a watchlist row, click the customer name → Customer 360. From an outlier dot in a scatter, click → project drill-down.",
      },
      {
        q: "Can I export the data?",
        a: "Yes. Each card has an export action where applicable: CSV for tabular data, PNG / SVG for charts. The Decision Pack and Zen Insights brief support PDF export with audit trail attached.",
      },
      {
        q: "What keyboard shortcuts are supported?",
        a: "Cmd/Ctrl+K opens search across customers and projects. Esc dismisses any open modal or drawer. Tab navigates through interactive controls in reading order. Arrow keys traverse data tables when focused.",
      },
    ],
  },
  {
    label: "Glossary & Domain",
    items: [
      {
        q: "Where do UK terms (CIS, vesting, JCT, etc.) come from?",
        a: "The Glossary modal (footer link) carries UK / US / cross-border construction-finance terminology. Eight entries cover CIS, vesting, retention-of-title, adjudication, pay-less notice, final account, JCT, NEC — each with a US analog where one exists. Pull it up whenever a UK contractor report lands in your inbox.",
      },
      {
        q: "What's the difference between EAC drift and margin fade?",
        a: "EAC drift (K-008) measures how much the current Estimate at Completion has moved relative to the original budget — a ratio. Margin fade (K-026, K-032) measures the dollar erosion of project margin from bid to current EAC, stratified by % complete bucket. Drift is the leading indicator; fade is the dollar consequence.",
      },
      {
        q: "What does \"pay-if-paid\" mean and why is it tracked?",
        a: "A pay-if-paid clause shifts the upstream-insolvency risk from the contractor to the subcontractor — payment downstream is contingent on the contractor receiving payment from the owner. K-029 tracks AR exposure on customers / contracts flagged pay-if-paid, since this is a quasi-contingent liability.",
      },
    ],
  },
];

export default function Faq() {
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filteredSections = q
    ? FAQ.map((s) => ({
        ...s,
        items: s.items.filter((it) => {
          const ans = Array.isArray(it.a) ? it.a.join(" ") : it.a;
          return (it.q + " " + ans).toLowerCase().includes(q);
        }),
      })).filter((s) => s.items.length > 0)
    : FAQ;

  const totalShown = filteredSections.reduce((acc, s) => acc + s.items.length, 0);
  const totalAll = FAQ.reduce((acc, s) => acc + s.items.length, 0);

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">FAQ <span className="page-pill">{totalAll} QUESTIONS</span></h1>
          <p className="page-sub">
            Common questions about the platform, data sources, models, security, and how to drive
            decisions from the indicators surfaced. If something's missing, ask your account team.
          </p>
        </div>
      </div>

      <div className="faq-toolbar">
        <input
          type="search"
          className="faq-search"
          placeholder="Search the FAQ…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {q && <span className="faq-results-meta">{totalShown} of {totalAll} shown</span>}
      </div>

      {filteredSections.map((section) => (
        <section key={section.label} className="faq-section">
          <h2 className="faq-section-title">{section.label}</h2>
          <div className="faq-list">
            {section.items.map((it, i) => {
              const id = `${section.label}-${i}`;
              const isOpen = open === id;
              return (
                <article key={id} className={`faq-item${isOpen ? " open" : ""}`}>
                  <button
                    className="faq-q"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? null : id)}
                  >
                    <span className="faq-q-text">{it.q}</span>
                    <span className="faq-q-icon" aria-hidden="true">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="faq-a">
                      {Array.isArray(it.a) ? (
                        <ul>
                          {it.a.map((line, j) => (
                            <li key={j}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>{it.a}</p>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </section>
  );
}
